import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { DoodProductModel } from '@core/models/product.model';
import { ModalsService } from '@core/services/modals/modals.service';
import { ProductService } from '@core/services/product/product.service';
import { ProductWithUIData } from '@shared/local-store/cart/cart-draft-declinable-product.store';
import { KioskProductCardAtomComponent } from '@shared/atoms/kiosk/kiosk-product-cards/kiosk-product-card-atom/kiosk-product-card-atom.component';
import { KioskProductOverviewModalComponent } from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';

@Component({
  selector: 'app-simple-product-card-organism',
  standalone: true,
  imports: [CommonModule, ContentBuilderColorPipe, KioskProductCardAtomComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (simpleProduct(); as product) {
      <div
        class="h-full rounded"
        [ngClass]="{
          'outline outline-[8px]': product.quantity,
          'outline-red-600': isCartValid()?.isValid === false,
        }"
        [style.outlineColor]="buttonBackgroundColor() | contentBuilderColor"
      >
        <app-kiosk-product-card-atom
          (click)="onCardClicked()"
          [class]="optionCardType()"
          [product]="product"
          [quantity]="product.quantity"
          [isProductAvailable]="isAvailable(product)"
          (changeProductQuantity)="onChangeProductQuantity($event)"
          [selectionnable]="isAvailable(product)"
          [displayPrice]="false"
          [displayedQuantity]="product.quantity ?? 0"
          [textColor]="textColor()"
          [cardColor]="cardColor()"
          [unavailableButtonBackground]="unavailableButtonBackground()"
          [unavailableButtonText]="unavailableButtonText()"
          [buttonBackgroundColor]="buttonBackgroundColor()"
          [buttonTextColor]="buttonTextColor()"
          [secondaryColor]="secondaryColor()"
          [maxProduct]="maxProductQuantity() ?? 1"
          [monoSelection]="maxProductQuantity() === 1"
          [displayedAddition]="product?.uiPrice"
          (showProduct)="openProductModal(product)"
        ></app-kiosk-product-card-atom>
      </div>
    }
  `,
})
export class SimpleProductCardOrganismComponent {
  private readonly productService = inject(ProductService);

  public readonly cardColor = input<IContentBuilderFieldColor>();
  public readonly textColor = input<IContentBuilderFieldColor>();
  public readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  public readonly buttonTextColor = input<IContentBuilderFieldColor>();
  public readonly backgroundColor = input<IContentBuilderFieldColor>();
  public readonly primaryButtonBackgroundColor = input<IContentBuilderFieldColor>();
  public readonly primaryButtonTextColor = input<IContentBuilderFieldColor>();
  public readonly secondaryColor = input<IContentBuilderFieldColor>();
  public readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  public readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  public readonly simpleProduct = input.required<Readonly<ProductWithUIData>>();
  public readonly maxProductQuantity = input<number>();
  public readonly isCartValid = input<{ isValid: boolean }>();
  public readonly showProductAdditions = output<{ selectedProductId: string }>();
  public readonly changeQuantity = output<number>();

  private readonly modalsService = inject(ModalsService);
  private readonly hasAdditions = computed(() => {
    return this.simpleProduct()?.additions.products.length > 0;
  });
  private onShowProductAdditions() {
    const additions = this.simpleProduct()?.additions.products;
    if (!additions || !additions.length) {
      return;
    }
    const product = this.simpleProduct();
    if (!product) {
      return;
    }
    this.showProductAdditions.emit({ selectedProductId: product.id });
  }
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);

  protected isAvailable(product: DoodProductModel): boolean {
    return this.productService.isAvailable(product);
  }

  protected onChangeProductQuantity(quantity: number) {
    this.changeQuantity.emit(quantity);
  }
  protected onCardClicked() {
    if (!this.isAvailable(this.simpleProduct())) {
      return;
    }
    if (this.hasAdditions()) {
      this.onShowProductAdditions();

      return;
    }
  }
  protected readonly optionCardType = computed(() => {
    const type = this.kioskGlobalSettingsService.settings().productCard.styles.type;
    return type === 'defaultCard' ? '' : type;
  });

  openProductModal(product: DoodProductModel): void {
    this.modalsService.open(KioskProductOverviewModalComponent.handle, undefined, {
      image: product.images?.cover?.url,
      name: product.name,
      description: product.description,
      allergens: product.allergens,
      type: product.type,
      backgroundColor: this.buttonTextColor(),
      accentColor: this.secondaryColor(),
    });
  }

  // todo fix
  forceType(data: any): any {
    return data as any;
  }
}
